import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Check, X, Zap, Crown, Sparkles, Star, Coins } from 'lucide-react'
import TopUpModal from '../TopUpModal'
import UpgradeModal from '../UpgradeModal'
import LifetimeCheckoutModal from '../LifetimeCheckoutModal'
import { getUser, isProUser, getSubscriptionDetails } from '../../utils/auth'

const plans = [
    {
        name: 'Free',
        price: '$0',
        period: 'forever',
        description: 'Perfect for occasional writers and trying out InkSpot',
        icon: Zap,
        features: [
            { name: '10 AI detections per day', included: true },
            { name: 'Basic cliché detection', included: true },
            { name: 'Web app access', included: true },
            { name: 'Chrome extension', included: true },
            { name: '500+ built-in patterns', included: true },
            { name: 'AI suggestions', included: false },
            { name: 'Custom style guides', included: false },
            { name: 'Analytics dashboard', included: false },
            { name: 'Priority support', included: false },
        ],
        cta: 'Start Free',
        ctaLink: '/signup',
        featured: false,
    },
    {
        name: 'Pay-as-you-go',
        price: '$3',
        period: 'per 50k tokens',
        description: 'Flexible credits for sporadic power users. No monthly fees.',
        icon: Coins,
        features: [
            { name: 'Use credits anytime', included: true },
            { name: 'Advanced cliché detection', included: true },
            { name: 'Web app access', included: true },
            { name: 'Chrome extension', included: true },
            { name: 'Access to Pro features', included: true },
            { name: 'AI-powered suggestions', included: true },
            { name: 'Detailed analytics', included: true },
            { name: 'Credits never expire', included: true },
            { name: 'Priority support', included: true },
        ],
        cta: 'Buy Credits',
        ctaLink: '#',
        isTopUp: true,
        featured: false,
    },
    {
        name: 'Pro',
        price: '$3',
        annualPrice: '$1.50',
        period: 'per month',
        description: 'For serious writers who want to level up their content',
        icon: Crown,
        badge: 'Most Popular',
        features: [
            { name: 'Unlimited AI detections', included: true },
            { name: 'Advanced cliché detection', included: true },
            { name: 'Web app access', included: true },
            { name: 'Chrome extension', included: true },
            { name: '2,000+ patterns library', included: true },
            { name: 'AI-powered suggestions', included: true },
            { name: 'Custom style guides', included: true },
            { name: 'Full analytics dashboard', included: true },
            { name: 'Priority email support', included: true },
        ],
        cta: 'Get Pro',
        ctaLink: '/signup?plan=pro',
        featured: true,
        paypalPlanId: 'P-21G26709CS318545FNH4WY7I',
    },
    {
        name: 'Founding Member',
        price: '$29',
        period: 'one-time',
        description: 'Lock in lifetime Pro access at our lowest price ever',
        icon: Star,
        badge: '🔥 Limited Time',
        isLifetime: true,
        features: [
            { name: 'Unlimited AI detections', included: true },
            { name: 'Advanced cliché detection', included: true },
            { name: 'Web app access', included: true },
            { name: 'Chrome extension', included: true },
            { name: '2,000+ patterns library', included: true },
            { name: 'AI-powered suggestions', included: true },
            { name: 'Custom style guides', included: true },
            { name: 'Full analytics dashboard', included: true },
            { name: 'Priority email support', included: true },
            { name: 'All future Pro features', included: true },
        ],
        cta: 'Get Lifetime Access',
        ctaLink: '/signup?plan=lifetime',
        featured: false,
    },
]

export default function Pricing() {
    const navigate = useNavigate()
    const [isAnnual, setIsAnnual] = useState(false)
    const [showTopUpModal, setShowTopUpModal] = useState(false)
    const [showUpgradeModal, setShowUpgradeModal] = useState(false)
    const [showLifetimeModal, setShowLifetimeModal] = useState(false)
    const [user, setUser] = useState(null)
    const [currentPlan, setCurrentPlan] = useState('free') // 'free' | 'pro' | 'lifetime'

    useEffect(() => {
        const checkAuth = async () => {
            const currentUser = await getUser()
            setUser(currentUser)
            if (currentUser) {
                const subDetails = await getSubscriptionDetails(currentUser.id, currentUser.email)
                if (subDetails?.plan === 'lifetime') {
                    setCurrentPlan('lifetime')
                } else if (subDetails?.plan === 'pro') {
                    // Check if subscription is truly active (not just status field)
                    const isActive = subDetails.status === 'active'
                    const isCancelledButValid = subDetails.status === 'cancelled' &&
                        subDetails.expires_at && new Date(subDetails.expires_at) > new Date()

                    // Safety net: check billing date hasn't expired past grace period
                    const GRACE_DAYS = 7
                    let billingExpired = false
                    if (isActive && subDetails.next_billing_date) {
                        const billingDate = new Date(subDetails.next_billing_date)
                        const graceCutoff = new Date()
                        graceCutoff.setDate(graceCutoff.getDate() - GRACE_DAYS)
                        billingExpired = billingDate < graceCutoff
                    }

                    if ((isActive && !billingExpired) || isCancelledButValid) {
                        setCurrentPlan('pro')
                    } else {
                        setCurrentPlan('free')
                    }
                } else {
                    setCurrentPlan('free')
                }
            }
        }
        checkAuth()
    }, [])

    return (
        <section id="pricing" className="relative py-24 sm:py-32 overflow-hidden">
            {/* Background */}
            <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" />
            <div className="absolute inset-0 bg-dot-pattern opacity-20" />

            {/* Decorative Elements */}
            <div className="absolute top-20 left-10 w-72 h-72 bg-primary-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent-500/10 rounded-full blur-3xl" />

            {/* Content */}
            <div className="relative z-10 section-container">
                {/* Section Header */}
                <div className="text-center mb-16">
                    <h2 className="section-title">
                        Simple, Transparent{' '}
                        <span className="gradient-text">Pricing</span>
                    </h2>
                    <p className="section-subtitle">
                        Start free and upgrade when you're ready. No hidden fees, cancel anytime.
                    </p>

                    {/* Billing Toggle */}
                    <div className="flex items-center justify-center gap-4 mt-8">
                        <span className={`text-sm font-medium transition-colors ${!isAnnual ? 'text-white' : 'text-white/50'}`}>Monthly</span>
                        <button
                            onClick={() => setIsAnnual(!isAnnual)}
                            className={`relative w-14 h-8 rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-slate-900
                         ${isAnnual ? 'bg-gradient-to-r from-primary-500 to-accent-500' : 'bg-white/20'}`}
                            role="switch"
                            aria-checked={isAnnual}
                        >
                            <span
                                className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow-lg transition-all duration-300 ease-in-out
                           ${isAnnual ? 'translate-x-6' : 'translate-x-0'}`}
                            />
                        </button>
                        <span className={`text-sm font-medium transition-colors ${isAnnual ? 'text-white' : 'text-white/50'}`}>
                            Annual
                            <span className="ml-2 text-xs text-primary-400 font-semibold bg-primary-500/10 px-2 py-0.5 rounded-full">Save 50%</span>
                        </span>
                    </div>
                </div>

                {/* Pricing Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8 max-w-7xl mx-auto">
                    {plans.map((plan) => {
                        // Determine if this is the user's current plan
                        const isCurrentPlan = user && (
                            (plan.name === 'Free' && currentPlan === 'free') ||
                            (plan.name === 'Pro' && currentPlan === 'pro') ||
                            (plan.name === 'Founding Member' && currentPlan === 'lifetime')
                        )

                        return (
                            <div
                                key={plan.name}
                                className={`relative ${plan.featured ? 'pricing-card-featured pt-8' : 'pricing-card'} 
                         ${isCurrentPlan ? 'ring-2 ring-primary-400/50' : ''}
                         transform transition-all duration-300 hover:scale-[1.02] hover:shadow-xl`}
                            >
                                {/* Current Plan Badge */}
                                {isCurrentPlan && !plan.badge && (
                                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                                        <div className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 text-white text-sm font-medium shadow-lg">
                                            <Check className="w-4 h-4" />
                                            Your Plan
                                        </div>
                                    </div>
                                )}

                                {/* Badge */}
                                {plan.badge && (
                                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                                        <div className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-white text-sm font-medium shadow-lg ${isCurrentPlan ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gradient-to-r from-primary-500 to-accent-500'}`}>
                                            {isCurrentPlan ? <Check className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
                                            {isCurrentPlan ? 'Your Plan' : plan.badge}
                                        </div>
                                    </div>
                                )}

                                {/* Header */}
                                <div className="flex items-center gap-3 mb-6">
                                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center
                              ${plan.featured ? 'bg-primary-500/20' : 'bg-white/10'}`}>
                                        <plan.icon className={`w-6 h-6 ${plan.featured ? 'text-primary-400' : 'text-white/60'}`} />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-white">{plan.name}</h3>
                                        <p className="text-sm text-white/50">{plan.description}</p>
                                    </div>
                                </div>

                                {/* Price */}
                                <div className="mb-6">
                                    <div className="flex items-baseline gap-2">
                                        <span className="text-5xl font-bold gradient-text">
                                            {plan.annualPrice && isAnnual ? plan.annualPrice : plan.price}
                                        </span>
                                        <span className="text-white/50">/ {plan.period}</span>
                                    </div>
                                    {isAnnual && plan.annualPrice && (
                                        <p className="text-sm text-primary-400 mt-1">
                                            Billed annually (${parseFloat(plan.annualPrice.replace('$', '')) * 12}/year)
                                        </p>
                                    )}
                                </div>

                                {/* Features */}
                                <ul className="space-y-3 mb-8">
                                    {plan.features.map((feature) => (
                                        <li key={feature.name} className="flex items-start gap-3">
                                            {feature.included ? (
                                                <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                                            ) : (
                                                <X className="w-5 h-5 text-white/30 flex-shrink-0 mt-0.5" />
                                            )}
                                            <span className={feature.included ? 'text-white/80' : 'text-white/40'}>
                                                {feature.name}
                                            </span>
                                        </li>
                                    ))}
                                </ul>

                                {/* CTA Button */}
                                {isCurrentPlan ? (
                                    <div className="w-full text-center py-3 px-6 rounded-xl bg-white/10 text-white/60 font-medium cursor-default">
                                        Current Plan
                                    </div>
                                ) : plan.isTopUp ? (
                                    <button
                                        onClick={() => {
                                            if (!user) {
                                                navigate('/signin?redirect=/#pricing')
                                                return
                                            }
                                            setShowTopUpModal(true)
                                        }}
                                        className={`w-full text-center ${plan.featured ? 'btn-primary' : 'btn-secondary'}`}
                                    >
                                        {plan.cta}
                                    </button>
                                ) : plan.name === 'Free' ? (
                                    <Link
                                        to={user ? '/app' : plan.ctaLink}
                                        className={`w-full text-center ${plan.featured ? 'btn-primary' : 'btn-secondary'}`}
                                    >
                                        {user ? 'Go to App' : plan.cta}
                                    </Link>
                                ) : plan.name === 'Pro' ? (
                                    <button
                                        onClick={() => {
                                            if (!user) {
                                                navigate('/signup?plan=pro')
                                                return
                                            }
                                            setShowUpgradeModal(true)
                                        }}
                                        className={`w-full text-center ${plan.featured ? 'btn-primary' : 'btn-secondary'}`}
                                    >
                                        {plan.cta}
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => {
                                            if (!user) {
                                                navigate('/signup?plan=lifetime')
                                                return
                                            }
                                            setShowLifetimeModal(true)
                                        }}
                                        className={`w-full text-center ${plan.featured ? 'btn-primary' : 'btn-secondary'}`}
                                    >
                                        {plan.cta}
                                    </button>
                                )}
                            </div>
                        )
                    })}
                </div>

                {/* Money-back guarantee */}
                <div className="mt-12 text-center">
                    <p className="text-white/50 text-sm">
                        💰 30-day money-back guarantee • 🔒 Secure payment via PayPal
                    </p>
                </div>

                <TopUpModal
                    isOpen={showTopUpModal}
                    onClose={() => setShowTopUpModal(false)}
                    userEmail={user?.email}
                />

                <UpgradeModal
                    isOpen={showUpgradeModal}
                    onClose={() => setShowUpgradeModal(false)}
                    isAnnual={isAnnual}
                />

                <LifetimeCheckoutModal
                    isOpen={showLifetimeModal}
                    onClose={() => setShowLifetimeModal(false)}
                    userEmail={user?.email}
                />
            </div>
        </section>
    )
}
